<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useReducedMotion } from '@/composables/useReducedMotion'
import {
  buildMarkMotes,
  buildMotes,
  clamp01,
  crestAt,
  layoutField,
  MARK_POUR,
  SPHERE_HOLD_TIME,
  SPHERE_RADIUS,
  tempoSpeed,
  type FieldPoint,
  type Mote,
  type PourTimes,
} from './particle-ring-field'

const props = withDefaults(
  defineProps<{
    /** Ring energy: `calm` drifts, `quick` drifts faster, `still` contracts and waits. */
    tempo?: 'calm' | 'quick' | 'still'
    /** `accent` is the brand hue; `muted` drains the field to neutral. */
    tone?: 'accent' | 'muted' | 'danger'
    /** Holds the field mid-drift - for a run that has stopped moving on its own. */
    frozen?: boolean
    /** Plays the one-shot outward burst, then emits `burst-end`. */
    burst?: boolean
    /** Seeds the scatter, so a given ring looks identical on every render. */
    seed?: number
    /** `field` is the full-size ring with its crest sweep; `mark` is the 24 px ball of 44 dots that hold a ring, pour into a turning sphere and pour back. */
    variant?: 'field' | 'mark'
  }>(),
  {
    tempo: 'calm',
    tone: 'accent',
    frozen: false,
    burst: false,
    seed: 0x5eed,
    variant: 'field',
  },
)

const emit = defineEmits<{
  burstEnd: []
}>()

interface Dot {
  radius: number
  alpha: number
}

interface VariantStyle {
  build: (seed: number) => Mote[]
  turn: 'plane' | 'globe'
  pour?: PourTimes
  stillTime: number
  dot: (t: number, point: FieldPoint, mote: Mote, scale: number) => Dot
}

const FIELD_ALPHA = 0.45
const MARK_RING_RADIUS = 3.5
const MARK_RING_ALPHA = 0.45
const MARK_FAR_RADIUS = 2.3
const MARK_NEAR_RADIUS = 5.4
const MARK_FAR_ALPHA = 0.2

function lerp(from: number, to: number, k: number): number {
  return from + (to - from) * k
}

const DENSITY = 8
const GLOW_SPREAD = 1.5
const GLOW_ALPHA = 0.16
const TAU = Math.PI * 2

const VARIANTS: Record<'field' | 'mark', VariantStyle> = {
  field: {
    build: (seed) => buildMotes(seed, DENSITY),
    turn: 'plane',
    stillTime: 0,
    dot: (t, point, mote, scale) => {
      const { op, sc } = crestAt(t, mote)
      const depth = clamp01((point.z + 45) / 90)
      const light = 0.65 + depth * 0.9
      return {
        radius: mote.r * scale * sc * (0.85 + depth * 0.5) * point.per,
        alpha: op * FIELD_ALPHA * mote.strength * light,
      }
    },
  },
  mark: {
    build: (seed) => buildMarkMotes(seed),
    turn: 'globe',
    pour: MARK_POUR,
    stillTime: SPHERE_HOLD_TIME,
    dot: (_t, point, mote, scale) => {
      const near = clamp01((point.z + SPHERE_RADIUS) / (2 * SPHERE_RADIUS))
      const ringAlpha = MARK_RING_ALPHA + (1 - MARK_RING_ALPHA) * mote.strength
      return {
        radius:
          lerp(MARK_RING_RADIUS, lerp(MARK_FAR_RADIUS, MARK_NEAR_RADIUS, near), point.mix) * scale,
        alpha: lerp(ringAlpha, lerp(MARK_FAR_ALPHA, 1, near), point.mix),
      }
    },
  },
}

const canvas = ref<HTMLCanvasElement | null>(null)
const style = computed(() => VARIANTS[props.variant])
const motes = computed<Mote[]>(() => style.value.build(props.seed))

const reduced = useReducedMotion()
const onScreen = ref(true)
const pageVisible = ref(typeof document === 'undefined' || document.visibilityState !== 'hidden')
const moving = computed(() => !reduced.value && !props.frozen)
const running = computed(() => moving.value && onScreen.value && pageVisible.value)

let frameId = 0
let lastFrameAt = 0
let fieldTime = 0
let color = 'rgb(0, 0, 0)'
let colorReadAt = -Infinity

const TONE_CLASSES = {
  accent: 'text-accent-blue',
  muted: 'text-text-tertiary',
  danger: 'text-accent-red',
} as const
let width = 0
let height = 0
let resizeObserver: ResizeObserver | null = null
let intersectionObserver: IntersectionObserver | null = null

function readColor(el: HTMLCanvasElement): string {
  return getComputedStyle(el).color || color
}

function resize(el: HTMLCanvasElement): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const rect = el.getBoundingClientRect()
  width = Math.max(1, Math.round(rect.width * dpr))
  height = Math.max(1, Math.round(rect.height * dpr))
  el.width = width
  el.height = height
}

function draw(now: number): void {
  const el = canvas.value
  if (!el) return
  const ctx = el.getContext('2d')
  if (!ctx) return

  if (now - colorReadAt > 1000) {
    color = readColor(el)
    colorReadAt = now
  }

  const variant = style.value
  if (moving.value) {
    const dt = Math.max(0, Math.min(0.05, (now - lastFrameAt) / 1000))
    fieldTime += dt * tempoSpeed(props.tempo)
  } else if (reduced.value) {
    fieldTime = variant.stillTime
  }
  lastFrameAt = now

  ctx.clearRect(0, 0, width, height)

  const scale = (Math.min(width, height) / 100) * 1.02
  const cx = width / 2
  const cy = height / 2
  ctx.fillStyle = color
  const list = motes.value
  const points = layoutField(list, fieldTime, {
    tempo: props.tempo,
    turn: variant.turn,
    pour: variant.pour,
  })

  const drawn: Array<{ point: FieldPoint; mote: Mote }> = list.map((mote, index) => ({
    point: points[index]!,
    mote,
  }))
  drawn.sort((a, z) => a.point.z - z.point.z)

  for (const { point, mote } of drawn) {
    const dot = variant.dot(fieldTime, point, mote, scale)
    const alpha = clamp01(dot.alpha)
    const x = cx + (point.x - 50) * scale
    const y = cy + (point.y - 50) * scale

    ctx.globalAlpha = alpha * GLOW_ALPHA
    ctx.beginPath()
    ctx.arc(x, y, dot.radius * GLOW_SPREAD, 0, TAU)
    ctx.fill()

    ctx.globalAlpha = alpha
    ctx.beginPath()
    ctx.arc(x, y, dot.radius, 0, TAU)
    ctx.fill()
  }
  ctx.globalAlpha = 1
}

function frame(now: number): void {
  draw(now)
  frameId = requestAnimationFrame(frame)
}

function stop(): void {
  if (frameId) cancelAnimationFrame(frameId)
  frameId = 0
}

function start(): void {
  if (frameId) return
  lastFrameAt = performance.now()
  frameId = requestAnimationFrame(frame)
}

function onVisibilityChange(): void {
  pageVisible.value = document.visibilityState !== 'hidden'
}

watch(running, (run) => {
  if (run) start()
  else {
    stop()
    draw(performance.now())
  }
})

onMounted(() => {
  const el = canvas.value
  if (!el) return
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      resize(el)
      if (!frameId) draw(lastFrameAt)
    })
    resizeObserver.observe(el)
  }
  if (typeof IntersectionObserver !== 'undefined') {
    intersectionObserver = new IntersectionObserver(([entry]) => {
      onScreen.value = entry?.isIntersecting ?? true
    })
    intersectionObserver.observe(el)
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  resize(el)
  lastFrameAt = performance.now()
  if (running.value) start()
  else draw(lastFrameAt)
})

onBeforeUnmount(() => {
  stop()
  resizeObserver?.disconnect()
  intersectionObserver?.disconnect()
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

function onAnimationEnd(event: AnimationEvent): void {
  if (event.animationName.startsWith('ring-burst') || event.animationName.startsWith('fade-out')) {
    emit('burstEnd')
  }
}
</script>

<template>
  <div
    :class="
      variant === 'mark' ? 'inline-grid size-12 shrink-0 place-items-center' : 'particle-ring'
    "
    :data-variant="variant"
    :data-tempo="tempo"
    :data-tone="tone"
    :data-frozen="frozen ? 'true' : 'false'"
    :data-burst="burst ? 'true' : 'false'"
  >
    <canvas
      ref="canvas"
      :class="[
        variant === 'mark' ? 'block size-full' : 'particle-ring__canvas',
        TONE_CLASSES[tone],
      ]"
      aria-hidden="true"
      @animationend="onAnimationEnd"
    />

    <slot />
  </div>
</template>
